import {
  FieldErrorText,
  FieldLabel,
  Flex,
  HStack,
  Input,
  Link,
  Text,
  chakra,
} from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { SupportedCreditVariationList } from '~/components/common/credits/SupportedCreditVariationList';
import { useI18nLn } from '~/hooks/store';

import { Field } from '~/components/ui/field';
import {
  NativeSelectField,
  NativeSelectRoot,
} from '~/components/ui/native-select';
import { PasswordInput } from '~/components/ui/password-input';
import type { CreditFormValue } from '~/types/components';

type Props = {
  onSubmit: (data: CreditFormValue) => Promise<void>;
  formId: string;
};

const SECURITY_CODE_DESCRIPTION_URL =
  'https://www.gmo-pg.com/service/mulpay/security/securitycode/#:~:text=%E3%82%AF%E3%83%AC%E3%82%B8%E3%83%83%E3%83%88%E3%82%AB%E3%83%BC%E3%83%89%E6%B1%BA%E6%B8%88%E3%82%92%E5%88%A9%E7%94%A8,%E3%81%8A%E3%82%88%E3%81%B3%E5%90%8D%E7%A7%B0%E3%81%8C%E7%95%B0%E3%81%AA%E3%82%8A%E3%81%BE%E3%81%99%E3%80%82&text=%E3%82%AB%E3%83%BC%E3%83%89%E8%A3%8F%E9%9D%A2%E3%81%AE%E6%9C%AB%E5%B0%BE3%E6%A1%81%E3%81%AE%E6%95%B0%E5%AD%97%E3%81%8C%E3%82%BB%E3%82%AD%E3%83%A5%E3%83%AA%E3%83%86%E3%82%A3%E3%82%B3%E3%83%BC%E3%83%89%E3%81%A7%E3%81%99%E3%80%82';

export const CreditForm: React.FC<Props> = (props: Props) => {
  const {
    clearErrors,
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext<CreditFormValue>();
  const { t } = useI18nLn();

  const ccExpirationMonth = () => {
    return Array.from(Array(12).keys()).map((i) => {
      const month = (i + 1).toString().padStart(2, '0');
      return (
        <option key={month} value={month}>
          {month}
        </option>
      );
    });
  };

  // 現在から10年後までの年のリストを作成
  const ccExpirationYear = () => {
    const currentYear = new Date().getFullYear();
    return Array.from(Array(11).keys()).map((i) => {
      const year = (currentYear + i).toString().slice(-2);
      return (
        <option key={year} value={year}>
          {year}
        </option>
      );
    });
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)} id={props.formId}>
      <Field
        pb={errors.card_number ? '0' : '22px'}
        invalid={!!errors.card_number}
        required={true}
      >
        <FieldLabel alignItems="center" justifyContent="space-around">
          <Text mr={3} fontSize="xs">
            {t('pages.buy.payments.credit.cardno')}
            <chakra.span ml={1} fontSize="14px" color="red">
              *
            </chakra.span>
          </Text>
          <SupportedCreditVariationList />
        </FieldLabel>

        <Input
          variant="outline"
          inputMode="numeric"
          autoComplete="cc-number"
          autoCorrect="true"
          pattern="[0-9\s]{17,19}"
          form="####  ####  ####  #####"
          placeholder="0000  0000  0000  0000"
          maxLength={19}
          autoFocus={true}
          _focus={{
            borderColor: 'primary',
          }}
          {...register('card_number', {
            required: {
              value: true,
              message: '有効なカード番号を入力してください',
            },
            pattern: {
              value: /^[0-9\s]{17,19}$/, // 14~16桁の数字とスペース込みの桁数
              message: '有効なカード番号を入力してください',
            },
          })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.value = e.target.value.replace(/(\d{4})(?=\d)/g, '$1 ');
            const isValid = /^[0-9\s]{17,19}$/.test(e.target.value);
            if (isValid) {
              clearErrors('card_number');
            }
          }}
        />
        <FieldErrorText>{errors.card_number?.message}</FieldErrorText>
      </Field>
      <Field
        pt="8px"
        pb={
          errors.card_expire_month || errors.card_expire_year ? '0px' : '22px'
        }
        invalid={!!errors.card_expire_month || !!errors.card_expire_year}
        label={t('pages.buy.payments.credit.exp')}
        required={true}
      >
        <HStack alignItems="center" justifyContent="space-around">
          <NativeSelectRoot
            w={window.innerWidth <= 380 ? '72px' : '80px'}
            size="md"
            variant="outline"
            _focus={{
              borderColor: 'primary',
            }}
          >
            <NativeSelectField
              focusRingColor="primary"
              autoComplete="cc-exp-month"
              autoCorrect="true"
              placeholder="MM"
              {...register('card_expire_month', {
                required: {
                  value: true,
                  message: '正しい有効期限を入力してください',
                },
              })}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                if (e.target.value.length > 0) {
                  clearErrors('card_expire_month');
                }
              }}
            >
              {ccExpirationMonth()}
            </NativeSelectField>
          </NativeSelectRoot>

          <Text textAlign="center">/</Text>
          <NativeSelectRoot
            w={window.innerWidth <= 380 ? '72px' : '80px'}
            size="md"
            variant="outline"
            _focus={{
              borderColor: 'primary',
            }}
          >
            <NativeSelectField
              autoComplete="cc-exp-year"
              autoCorrect="true"
              focusRingColor="primary"
              placeholder="YY"
              {...register('card_expire_year', {
                required: {
                  value: true,
                  message: '正しい有効期限を入力してください',
                },
              })}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                if (e.target.value.length > 0) {
                  clearErrors('card_expire_year');
                }
              }}
            >
              {ccExpirationYear()}
            </NativeSelectField>
          </NativeSelectRoot>
        </HStack>
        <FieldErrorText>
          {errors.card_expire_month?.message ||
            errors.card_expire_year?.message}
        </FieldErrorText>
      </Field>
      <Field
        pt="8px"
        pb={errors.cardholder_name ? '0' : '22px'}
        invalid={!!errors.cardholder_name}
        label={t('pages.buy.payments.credit.holder_name')}
        required={true}
      >
        <Input
          variant="outline"
          type="string"
          autoComplete="cc-name"
          autoCorrect="true"
          textTransform="uppercase"
          pattern="[A-Z\s]+"
          placeholder="ROLL TAROU"
          _focus={{
            borderColor: 'primary',
          }}
          {...register('cardholder_name', {
            required: {
              value: true,
              message: 'カードに記載されている名義を入力してください',
            },
            pattern: {
              value: /^[A-Z\s]+$/,
              message: 'カードに記載されている名義を入力してください',
            },
          })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.value = e.target.value.toUpperCase();
            const isValid = /^[A-Z\s]+$/.test(e.target.value);
            if (isValid) {
              clearErrors('cardholder_name');
            }
          }}
        />
        <FieldErrorText>{errors.cardholder_name?.message}</FieldErrorText>
      </Field>
      <Field
        w={48}
        pt="8px"
        pb={errors.security_code ? '0' : '22px'}
        invalid={!!errors.security_code}
        label={t('pages.buy.payments.credit.security_code')}
        required={true}
      >
        <PasswordInput
          size="md"
          variant="outline"
          inputMode="numeric"
          autoComplete="cc-csc"
          autoCorrect="true"
          pattern="[0-9]{3,4}"
          placeholder="xxx"
          maxLength={4}
          _focus={{
            borderColor: 'primary',
          }}
          {...register('security_code', {
            required: {
              value: true,
              message: '有効なセキュリティコードを入力してください',
            },
            pattern: {
              value: /^[0-9]{3,4}$/,
              message: '有効なセキュリティコードを入力してください',
            },
          })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const isValid = /^[0-9]{3,4}$/.test(e.target.value);
            if (isValid) {
              clearErrors('security_code');
            }
          }}
        />
        <FieldErrorText>{errors.security_code?.message}</FieldErrorText>
      </Field>
      <Flex flexGrow={1} justifyContent="flex-end" alignItems="flex-end">
        <Link
          fontSize="xs"
          textDecoration="underline"
          href={SECURITY_CODE_DESCRIPTION_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('pages.buy.payments.credit.what_is_security_code')}
        </Link>
      </Flex>
    </form>
  );
};
